import { useSettings } from "contexts/SettingsManager/SettingsManager";
import { useMemo } from "react";
import styled from "styled-components/macro";
import { useFormatZoned, useSelectedDay } from "utils/customHooks/genericHooks";
import { isDstObserved, ONE_HOUR_OF_MS } from "utils/timeUtils";
import { useTimeZoneMsOffset } from "utils/timeZoneHooks";
import { timeZonesWikiData } from "utils/timeZonesWikiData";

/**
 * Tooltip contents that explains the day start offset and timezone override.
 *
 * @param showTimeZoneInfo - Whether to show the timezone override information (i.e. false if timezone is irrelevant to the context).
 */
export function TimeOffsetExplainer({
  showTimeZoneInfo,
}: {
  showTimeZoneInfo: boolean;
}) {
  const selectedDay = useSelectedDay();

  const formatZoned = useFormatZoned();
  const timeZoneMsOffset = useTimeZoneMsOffset();
  const { timeZoneLocaleString, dayStartMsOffset } = useSettings();
  const timeZoneUtcString = useMemo(() => {
    const wikiTz = timeZonesWikiData.find(
      row => row["TZ database name"] === timeZoneLocaleString
    )?.[isDstObserved() ? "UTC DST offset ±hh:mm" : "UTC offset ±hh:mm"];
    return `UTC${wikiTz}`;
  }, [timeZoneLocaleString]);
  const timeZoneLocaleStringFormatted = timeZoneLocaleString.split(":")[0];

  // Format offset display for tooltip
  const dayStartHours = Math.abs(dayStartMsOffset) / ONE_HOUR_OF_MS;
  const dayStartSign = dayStartMsOffset >= 0 ? "+" : "-";
  const dayStartFormatted =
    dayStartMsOffset !== 0 ? `${dayStartSign}${dayStartHours}h` : "";

  // For timezone calculations
  const timeZoneHours = Math.abs(timeZoneMsOffset) / ONE_HOUR_OF_MS;
  const timeZoneSign = timeZoneMsOffset >= 0 ? "+" : "-";

  // Default timezone is UTC-4
  // const defaultTimezoneFormatted = "UTC-4";
  const defaultTimezoneHours = 0;

  // Calculate relative offset (from default UTC-4 to the custom timezone)
  const relativeOffsetHours =
    timeZoneMsOffset !== 0
      ? timeZoneSign === "+"
        ? timeZoneHours + defaultTimezoneHours
        : defaultTimezoneHours - timeZoneHours
      : 0;
  const relativeOffsetSign = relativeOffsetHours >= 0 ? "+" : "-";
  const relativeOffsetFormatted =
    timeZoneMsOffset !== 0
      ? `${relativeOffsetSign}${Math.abs(relativeOffsetHours)}h`
      : "";

  const timeZoneFormatted =
    timeZoneMsOffset !== 0 ? timeZoneSign + timeZoneHours + "h" : "";

  const rangeStart = selectedDay.range.ge + dayStartMsOffset + timeZoneMsOffset;

  return (
    <TimeOffsetExplainerStyles data-testid="time-offset-explainer">
      {dayStartMsOffset !== 0 && (
        <>
          <p style={{ margin: "4px 0 12px" }}>
            {"*"} Day-start offset: <strong>{dayStartFormatted}</strong>
          </p>
          <p style={{ margin: "0 0 6px" }}>
            Result: 24-hour range starts at{" "}
            <strong>{formatZoned(rangeStart, "EEE HH:mm (MMM d)")}</strong>{" "}
            instead of {formatZoned(selectedDay.range.ge, "EEE HH:mm (MMM d)")}.
          </p>
        </>
      )}

      {showTimeZoneInfo && (
        <>
          {dayStartMsOffset !== 0 && timeZoneMsOffset !== 0 && (
            <>
              <hr style={{ margin: "16px -16px", opacity: 0.4 }} />
            </>
          )}
          {timeZoneMsOffset !== 0 && (
            <>
              <p style={{ margin: "4px 0 12px" }}>
                {"*"} Timezone override:{" "}
                <strong>{`${timeZoneLocaleStringFormatted} (${timeZoneUtcString})`}</strong>
              </p>
              <p style={{ margin: "0 0 6px" }}>
                This means displayed times are off by{" "}
                {`${relativeOffsetFormatted}${relativeOffsetFormatted !== timeZoneFormatted ? ` (${timeZoneFormatted})` : ""}`}{" "}
                from your home location.
              </p>
            </>
          )}
        </>
      )}
    </TimeOffsetExplainerStyles>
  );
}
const TimeOffsetExplainerStyles = styled.div`
  padding: 8px;
  max-width: 300px;
  font-size: 16px;
`;
